// React
import { useCallback } from 'react'

// Models
import { useStores } from 'models'

// Interfaces
import {
  IHomeCertificationAgencyGetAllAttrs,
  IHomeCertificationAgencyGetAttrs,
  IHomeCertificationAgencySaveAttrs,
  IHomeCertificationAgencyGetImagesAttrs,
  IHomeCertificationAgencyUpdateImageAttrs,
  IHomeCertificationAgencyDeleteImageAttrs,
} from 'interfaces/back-office/content-management/home/certification-agency/home-certification-agency-view.interface'

const useCertificationAgency = () => {
  // Hook
  const {
    homeCertificationAgency: {
      homeCertificationAgency_isLoading,
      homeCertificationAgency_isSaveLoading,
      homeCertificationAgency_isImageLoading,
      homeCertificationAgency_homeCertificationAgencies,
      homeCertificationAgency_detail,
      homeCertificationAgency_images,
      homeCertificationAgency_CLEAR_HOME_CERTIFICATION_AGENCIES,
      homeCertificationAgency_CLEAR_HOME_CERTIFICATION_AGENCY,
      homeCertificationAgency_CLEAR_IMAGES,
      homeCertificationAgency_SET_NEW_IMAGE,
      homeCertificationAgency_fetchHomeCertificationAgencies: certificationAgencyFetchList,
      homeCertificationAgency_fetchHomeCertificationAgency: certificationAgencyFetch,
      homeCertificationAgency_saveHomeCertificationAgency: certificationAgencySave,
      homeCertificationAgency_fetchImages: certificationAgencyFetchImages,
      homeCertificationAgency_updateImage: certificationAgencyUpdateImage,
      homeCertificationAgency_deleteImage: certificationAgencyDeleteImage,
    },
  } = useStores()

  /**
   * @description Fetch certification agency list
   *
   * @param {IHomeCertificationAgencyGetAllAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const homeCertificationAgency_fetchHomeCertificationAgencies = useCallback(
    (payload: IHomeCertificationAgencyGetAllAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        certificationAgencyFetchList({
          body: payload.body,
          requestConfig: payload.requestConfig,
          onSuccess: resolve,
          onError: reject,
        })
      })
    },
    // eslint-disable-next-line
    [],
  )

  /**
   * @description Fetch certification agency detail
   *
   * @param {IHomeCertificationAgencyGetAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const homeCertificationAgency_fetchHomeCertificationAgency = useCallback(
    (payload: IHomeCertificationAgencyGetAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        certificationAgencyFetch({
          params: payload.params,
          requestConfig: payload.requestConfig,
          onSuccess: resolve,
          onError: reject,
        })
      })
    },
    // eslint-disable-next-line
    [],
  )

  /**
   * @description Save certification agency
   *
   * @param {IHomeCertificationAgencySaveAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const homeCertificationAgency_saveHomeCertificationAgency = useCallback(
    (payload: IHomeCertificationAgencySaveAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        certificationAgencySave({
          params: payload.params,
          body: payload.body,
          requestConfig: payload.requestConfig,
          onSuccess: resolve,
          onError: reject,
        })
      })
    },
    // eslint-disable-next-line
    [],
  )

  /**
   * @description Get certification agency images
   *
   * @param {IHomeCertificationAgencyGetImagesAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const homeCertificationAgency_fetchImages = useCallback((payload: IHomeCertificationAgencyGetImagesAttrs): Promise<void> => {
    return new Promise((resolve, reject) => {
      certificationAgencyFetchImages({
        params: payload.params,
        requestConfig: payload.requestConfig,
        onSuccess: resolve,
        onError: reject,
      })
    })

    // eslint-disable-next-line
  }, [])

  /**
   * @description Update certification agency image
   *
   * @param {IHomeCertificationAgencyUpdateImageAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const homeCertificationAgency_updateImage = useCallback((payload: IHomeCertificationAgencyUpdateImageAttrs): Promise<void> => {
    return new Promise((resolve, reject) => {
      certificationAgencyUpdateImage({
        params: payload.params,
        body: payload.body,
        requestConfig: payload.requestConfig,
        onSuccess: resolve,
        onError: reject,
      })
    })

    // eslint-disable-next-line
  }, [])

  /**
   * @description Delete certification agency image
   *
   * @param {IHomeCertificationAgencyDeleteImageAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const homeCertificationAgency_deleteImage = useCallback((payload: IHomeCertificationAgencyDeleteImageAttrs): Promise<void> => {
    return new Promise((resolve, reject) => {
      certificationAgencyDeleteImage({
        params: payload.params,
        requestConfig: payload.requestConfig,
        onSuccess: resolve,
        onError: reject,
      })
    })

    // eslint-disable-next-line
  }, [])

  return {
    homeCertificationAgency_isLoading,
    homeCertificationAgency_isSaveLoading,
    homeCertificationAgency_isImageLoading,
    homeCertificationAgency_homeCertificationAgencies,
    homeCertificationAgency_detail,
    homeCertificationAgency_images,
    homeCertificationAgency_CLEAR_HOME_CERTIFICATION_AGENCIES,
    homeCertificationAgency_CLEAR_HOME_CERTIFICATION_AGENCY,
    homeCertificationAgency_CLEAR_IMAGES,
    homeCertificationAgency_SET_NEW_IMAGE,
    homeCertificationAgency_fetchHomeCertificationAgencies,
    homeCertificationAgency_fetchHomeCertificationAgency,
    homeCertificationAgency_saveHomeCertificationAgency,
    homeCertificationAgency_fetchImages,
    homeCertificationAgency_updateImage,
    homeCertificationAgency_deleteImage,
  }
}

export { useCertificationAgency }
